import type { FilteredNavItem, NavItem } from '@/data/nav-data'
import { type NavRouteTarget, resolveActualUrl } from '@/lib/navRoutes'
import type { PaletteCommand } from './types'

export type NavSection = 'project' | 'project-settings' | 'org' | 'org-settings'

const SECTION_LABELS: Record<NavSection, string> = {
	project: 'Project',
	'project-settings': 'Project settings',
	org: 'Organization',
	'org-settings': 'Organization settings',
}

export interface NavCommandEntry {
	id: string
	title: string
	description: string
	keywords: string[]
	icon?: PaletteCommand['icon']
	target: NavRouteTarget
	planLocked: boolean
	isTab: boolean
}

/** A parent that only groups modules (Data, Actions) and is not a page of its own. */
const isGroupingOnly = (item: NavItem) =>
	Boolean(item.items?.length) &&
	!item.moduleKey &&
	!item.resource &&
	!item.permissionKey &&
	!item.items?.some(child => child.path === item.path)

/**
 * One entry per page the sidebar can reach, plus one per tab other than the page's default. Built from
 * the same nav data and route resolution as the sidebar, so the palette never offers a page the sidebar
 * hides and never resolves it to another org or project.
 */
export function collectNavCommands(
	items: readonly FilteredNavItem[],
	section: NavSection,
	resolve: (path: string) => NavRouteTarget,
	parentKeywords: readonly string[] = [],
): NavCommandEntry[] {
	const description = SECTION_LABELS[section]
	const sectionKeywords = section.endsWith('settings') ? ['settings'] : []

	return items.flatMap(item => {
		if (isGroupingOnly(item)) {
			return collectNavCommands(item.items ?? [], section, resolve, [...parentKeywords, item.title])
		}

		const target = resolve(item.path)
		const url = resolveActualUrl(target)
		const keywords = [...parentKeywords, ...sectionKeywords, ...(item.keywords ?? [])]
		const planLocked = Boolean(item.locked)

		const page: NavCommandEntry = {
			id: `nav-${section}-${item.id}`,
			title: `Go to ${item.title}`,
			description,
			keywords,
			icon: item.icon,
			target,
			planLocked,
			isTab: false,
		}

		const tabs = (item.items ?? []).flatMap(child => {
			const childTarget = resolve(child.path)
			if (resolveActualUrl(childTarget) === url) {
				return []
			}
			return [
				{
					id: `nav-${section}-${item.id}-${child.id}`,
					title: `Go to ${item.title}: ${child.title}`,
					description,
					keywords: [...keywords, item.title, child.title],
					icon: child.icon ?? item.icon,
					target: childTarget,
					planLocked,
					isTab: true,
				},
			]
		})

		return [page, ...tabs]
	})
}
